import { useId, useState, useEffect } from 'react';
import styles from './ColorPicker.module.css';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

interface ColorPickerProps {
  label: string;
  value: string | null;
  defaultColor: string;
  onChange: (hex: string) => void;
  /** Helper text mostrato sotto al campo. */
  hint?: string;
  disabled?: boolean;
}

/**
 * Wrapper su native input[type=color] + input testuale hex sincronizzato.
 * Mostra swatch + nome colore. Validation hex inline (mostra warning se
 * il valore tipato manualmente non rispetta `#RRGGBB`).
 */
export function ColorPicker({
  label,
  value,
  defaultColor,
  onChange,
  hint,
  disabled,
}: ColorPickerProps) {
  const id = useId();
  const effective = value || defaultColor;
  const [hexText, setHexText] = useState<string>(effective);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setHexText(effective);
    setInvalid(false);
  }, [effective]);

  const commit = (raw: string) => {
    const trimmed = raw.trim();
    if (HEX_RE.test(trimmed)) {
      setInvalid(false);
      onChange(trimmed.toLowerCase());
    } else {
      setInvalid(true);
    }
  };

  return (
    <div className={styles.root}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.controls}>
        <input
          id={id}
          type="color"
          value={effective}
          onChange={(e) => {
            const v = e.target.value;
            setHexText(v);
            setInvalid(false);
            onChange(v);
          }}
          disabled={disabled}
          aria-label={label}
          className={styles.colorInput}
        />
        <input
          type="text"
          value={hexText}
          onChange={(e) => {
            const v = e.target.value;
            setHexText(v);
            // Tentativo commit live se hex valido
            if (HEX_RE.test(v.trim())) {
              setInvalid(false);
              onChange(v.trim().toLowerCase());
            } else {
              setInvalid(true);
            }
          }}
          onBlur={(e) => commit(e.target.value)}
          disabled={disabled}
          placeholder={defaultColor}
          aria-label={`${label} valore esadecimale`}
          spellCheck={false}
          maxLength={7}
          className={`${styles.hexInput} ${invalid ? styles.hexInputInvalid : ''}`}
        />
      </div>
      {invalid && (
        <div className={styles.warn}>Usa formato esadecimale, es. #0b8a3e</div>
      )}
      {!invalid && hint && <div className={styles.hint}>{hint}</div>}
    </div>
  );
}
